import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../baseQuery';
import {
  ChooseCustomerRequest,
  ChooseCustomerResponse,
  CodeConfirmationRequest,
  CodeConfirmationResponse,
  Customer,
  EmailConfirmationRequest,
  ForgotPasswordEmailRequest,
  LoginRequest,
  LoginResponse,
  PhoneConfirmationRequest,
  PhoneExistsRequest,
  RegistrationRequest,
  RegistrationResponse,
  ResetPasswordRequest,
  SendTwoFARequest,
  SendTwoFAResponse,
  VerifyTwoFARequest,
  VerifyTwoFAResponse,
} from './type';

export const authApi = createApi({
  reducerPath: 'authApi',
  tagTypes: ['Auth'],
  keepUnusedDataFor: 10,
  baseQuery: baseQueryWithReauth(),
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => {
        const storedDeviceToken =
          typeof window !== 'undefined'
            ? window.localStorage.getItem('device_token')
            : null;
        return {
          url: `${process.env.REACT_APP_API_URL}/api/auth/login`,
          method: 'POST',
          body,
          headers: {
            ...(storedDeviceToken ? { 'X-Device-Token': storedDeviceToken } : {}),
          },
        };
      },
    }),

    verifyTwoFA: build.mutation<VerifyTwoFAResponse, VerifyTwoFARequest>({
      query: (body) => {
        const storedDeviceToken =
          typeof window !== 'undefined'
            ? window.localStorage.getItem('device_token')
            : null;
        return {
          url: `${process.env.REACT_APP_API_URL}/api/auth/verify`,
          method: 'POST',
          body,
          headers: {
            ...(storedDeviceToken ? { 'X-Device-Token': storedDeviceToken } : {}),
          },
        };
      },
    }),

    sendTwoFA: build.mutation<SendTwoFAResponse, SendTwoFARequest>({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL}/api/auth/send-2fa`,
        method: 'POST',
        body,
      }),
    }),
    registration: build.mutation<RegistrationResponse, RegistrationRequest>({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL}/api/registration`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Auth'],
    }),

    sendEmailConfirmation: build.mutation<void, EmailConfirmationRequest>({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL}/api/registration/email-confirmation`,
        method: 'POST',
        body,
      }),
    }),

    sendPhoneConfirmation: build.mutation<void, PhoneConfirmationRequest>({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL}/api/registration/phone-confirmation`,
        method: 'POST',
        body,
      }),
    }),

    confirmEmail: build.query<CodeConfirmationResponse, CodeConfirmationRequest>({
      query: ({ email, code }) => ({
        url: `${process.env.REACT_APP_API_URL}/api/registration/email-confirmation`,
        method: 'GET',
        params: {
          email,
          code,
        },
      }),
    }),

    confirmPhone: build.query<CodeConfirmationResponse, CodeConfirmationRequest>({
      query: ({ phone, code }) => ({
        url: `${process.env.REACT_APP_API_URL}/api/registration/phone-confirmation`,
        method: 'GET',
        params: {
          phone,
          code,
        },
      }),
    }),

    checkPhoneExists: build.query<void, PhoneExistsRequest>({
      query: ({ phone }) => ({
        url: `${process.env.REACT_APP_API_URL}/api/registration/phone-exists`,
        method: 'GET',
        params: {
          phone,
        },
      }),
    }),

    getCustomersList: build.query<Customer[], void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/api/customers-list`,
        method: 'GET',
      }),
      providesTags: ['Auth'],
    }),

    chooseCustomer: build.mutation<ChooseCustomerResponse, ChooseCustomerRequest>({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL}/api/auth/choose-customer`,
        method: 'POST',
        body,
      }),
    }),

    sendForgotPasswordEmail: build.mutation<void, ForgotPasswordEmailRequest>({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL}/api/auth/forgot-password`,
        method: 'POST',
        body,
      }),
    }),

    resetPassword: build.mutation<void, ResetPasswordRequest>({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL}/api/auth/reset-password`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Auth'],
    }),
  }),
});

export const {
  useLoginMutation,
  useVerifyTwoFAMutation,
  useSendTwoFAMutation,
  useRegistrationMutation,
  useSendEmailConfirmationMutation,
  useSendPhoneConfirmationMutation,
  useConfirmEmailQuery,
  useLazyConfirmEmailQuery,
  useConfirmPhoneQuery,
  useLazyConfirmPhoneQuery,
  useCheckPhoneExistsQuery,
  useLazyCheckPhoneExistsQuery,
  useGetCustomersListQuery,
  useChooseCustomerMutation,
  useSendForgotPasswordEmailMutation,
  useResetPasswordMutation,
} = authApi;
